var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Animated, Dimensions, KeyboardAvoidingView, Pressable, Modal as RNModal, ScrollView, TouchableOpacity, View } from 'react-native';
import { BLACK_700, BLACK_900 } from '../../constants';
import { useBreakpoint, useKeyboard } from '../../hooks';
import { StateConfigContext } from '../../providers';
import { checkIsPlatform, getTestId } from '../../utils';
import { Lottie } from '../lottie';
import { Typography } from '../typography';
import { Cross, DraggableNotch, BackButton } from './components';
import { getModalProps, getModalStyles } from './ModalStyles';
import { closeWithAnimation, fetchTimeSpent, getPanResponder, openWithAnimation } from './ModalUtils';
export var Modal = forwardRef((props, ref) => {
  var stateContext = useContext(StateConfigContext);
  var modalProps = getModalProps(props, stateContext);
  var _useKeyboard = useKeyboard(),
    keyboardVisible = _useKeyboard.keyboardVisible,
    keyboardHeight = _useKeyboard.keyboardHeight,
    top = _useKeyboard.top,
    bottom = _useKeyboard.bottom;
  var modalStyles = getModalStyles(modalProps, keyboardVisible, keyboardHeight, top, bottom);
  var initialVisible = modalProps.initialVisible,
    onTopOfStatusBar = modalProps.onTopOfStatusBar,
    minDistanceToCloseRatio = modalProps.minDistanceToCloseRatio,
    handleOnRequestClose = modalProps.handleOnRequestClose,
    onOpenHandler = modalProps.onOpenHandler,
    onCloseHandler = modalProps.onCloseHandler,
    keyboardAvoidingViewEnabled = modalProps.keyboardAvoidingViewEnabled,
    closeOnPressMask = modalProps.closeOnPressMask,
    onCloseFromMaskHandler = modalProps.onCloseFromMaskHandler,
    animationDuration = modalProps.animationDuration,
    dragFromTopOnly = modalProps.dragFromTopOnly,
    children = modalProps.children,
    footerElement = modalProps.footerElement,
    globalElement = modalProps.globalElement,
    swipeEnabled = modalProps.swipeEnabled,
    showBackButton = modalProps.showBackButton,
    backBtnContainerStyle = modalProps.backBtnContainerStyle,
    onPressBackButton = modalProps.onPressBackButton,
    showCross = modalProps.showCross,
    showHeaderTitle = modalProps.showHeaderTitle,
    showSubTitle = modalProps.showSubTitle,
    subTitle = modalProps.subTitle,
    backBtnAccessibilityProps = modalProps.backBtnAccessibilityProps,
    _modalProps$hideSubTi = modalProps.hideSubTitleOnSmallScreen,
    hideSubTitleOnSmallScreen = _modalProps$hideSubTi === void 0 ? false : _modalProps$hideSubTi,
    headerTitle = modalProps.headerTitle,
    headerTitleMultiText = modalProps.headerTitleMultiText,
    _modalProps$headerAli = modalProps.headerAlignment,
    headerAlignment = _modalProps$headerAli === void 0 ? 'center' : _modalProps$headerAli,
    wrapperStyle = modalProps.wrapperStyle,
    containerStyle = modalProps.containerStyle,
    crossContainerStyle = modalProps.crossContainerStyle,
    containerProps = modalProps.containerProps,
    config = modalProps.config,
    isScrollView = modalProps.isScrollView,
    scrollViewProps = modalProps.scrollViewProps,
    _modalProps$testID = modalProps.testID,
    testID = _modalProps$testID === void 0 ? 'Modal' : _modalProps$testID,
    crossIconAccessibilityProps = modalProps.crossIconAccessibilityProps,
    showLottieOnModal = modalProps.showLottieOnModal,
    lottieOnModalProps = modalProps.lottieOnModalProps,
    headerAccessibilityProps = modalProps.headerAccessibilityProps,
    subTitleAccessibilityProps = modalProps.subTitleAccessibilityProps,
    forceDesktopModal = modalProps.forceDesktopModal,
    restProps = __rest(modalProps, ["initialVisible", "onTopOfStatusBar", "minDistanceToCloseRatio", "handleOnRequestClose", "onOpenHandler", "onCloseHandler", "keyboardAvoidingViewEnabled", "closeOnPressMask", "onCloseFromMaskHandler", "animationDuration", "dragFromTopOnly", "children", "footerElement", "globalElement", "swipeEnabled", "showBackButton", "backBtnContainerStyle", "onPressBackButton", "showCross", "showHeaderTitle", "showSubTitle", "subTitle", "backBtnAccessibilityProps", "hideSubTitleOnSmallScreen", "headerTitle", "headerTitleMultiText", "headerAlignment", "wrapperStyle", "containerStyle", "crossContainerStyle", "containerProps", "config", "isScrollView", "scrollViewProps", "testID", "crossIconAccessibilityProps", "showLottieOnModal", "lottieOnModalProps", "headerAccessibilityProps", "subTitleAccessibilityProps", "forceDesktopModal"]);
  var startTime = useRef(0);
  var pageScroll = useRef(0);
  var isLargeScreen = useBreakpoint({
    returnAsObject: true,
    allowMdAsLargeScreen: true
  }).isLargeScreen;
  if (forceDesktopModal) {
    isLargeScreen = true;
  }
  var _useState = useState(initialVisible),
    isModalVisible = _useState[0],
    setIsModalVisible = _useState[1];
  var _useState2 = useState(260),
    currentHeight = _useState2[0],
    setCurrentHeight = _useState2[1];
  var checkSubTitleForLargeScreen = hideSubTitleOnSmallScreen ? isLargeScreen : true;
  var _useState3 = useState(new Animated.ValueXY({
      x: 0,
      y: currentHeight
    })),
    pan = _useState3[0];
  useEffect(() => {
    toggleModalVisibility(isModalVisible, 'swipe', false);
  }, []);
  useImperativeHandle(ref, () => ({
    close,
    open,
    isOpen
  }));
  useEffect(() => {
    if (checkIsPlatform('web')) {
      pageScroll.current = document.body.parentElement.scrollTop;
      document.body.style.overflow = isModalVisible ? 'hidden' : '';
    }
    return () => {
      if (checkIsPlatform('web')) {
        document.body.style.overflow = '';
        document.body.parentElement.scrollTop = pageScroll.current;
      }
    };
  }, [isModalVisible]);
  useEffect(() => {
    if (initialVisible) {
      !isModalVisible && open();
    } else {
      isModalVisible && close('swipe');
    }
  }, [initialVisible]);
  var open = () => {
    toggleModalVisibility(true);
  };
  var close = function close(closedBy) {
    if (closedBy === void 0) {
      closedBy = 'swipe';
    }
    toggleModalVisibility(false, closedBy, true);
  };
  var isOpen = () => {
    return isModalVisible;
  };
  var getLargeScreenHeight = useCallback(() => {
    return Dimensions.get('window').height / 2 + currentHeight;
  }, [currentHeight]);
  var toggleModalVisibility = function toggleModalVisibility(visible, closedBy, callCloseHandler) {
    if (closedBy === void 0) {
      closedBy = 'swipe';
    }
    if (callCloseHandler === void 0) {
      callCloseHandler = false;
    }
    if (visible) {
      callCloseHandler = true;
      startTime.current = Date.now();
      setIsModalVisible(true);
      openWithAnimation(pan, animationDuration, onOpenHandler);
    } else {
      var endTime = Date.now();
      var timeSpent = fetchTimeSpent(endTime, startTime.current, true);
      closeWithAnimation(pan, animationDuration, isLargeScreen ? getLargeScreenHeight() : currentHeight, () => {
        setIsModalVisible(false);
        callCloseHandler && (onCloseHandler === null || onCloseHandler === void 0 ? void 0 : onCloseHandler({
          closedBy,
          timeSpent
        }));
      });
    }
  };
  var handleChildrenLayout = event => {
    setCurrentHeight(event.nativeEvent.layout.height);
  };
  var panResponder = swipeEnabled && !isLargeScreen && getPanResponder(pan, minDistanceToCloseRatio, currentHeight, toggleModalVisibility);
  var animatedViewStyles = {
    transform: pan.getTranslateTransform()
  };
  var WrapperView = isLargeScreen ? TouchableOpacity : View;
  var Header = () => {
    return _jsxs(Animated.View, Object.assign({}, dragFromTopOnly && (panResponder === null || panResponder === void 0 ? void 0 : panResponder.panHandlers), {
      style: {
        zIndex: 1
      }
    }, {
      children: [_jsx(DraggableNotch, Object.assign({
        visible: swipeEnabled && !isLargeScreen,
        containerStyle: modalStyles.draggableContainer,
        panResponder: dragFromTopOnly && !isLargeScreen ? panResponder : null,
        notchStyle: modalStyles.notch
      }, getTestId(testID))), _jsxs(View, Object.assign({
        style: modalStyles.headerContainer
      }, {
        children: [_jsx(BackButton, Object.assign({
          isVisible: showBackButton,
          backBtnContainerStyles: [modalStyles.backContainerStyles, backBtnContainerStyle],
          backBtnIconProps: config === null || config === void 0 ? void 0 : config.backBtnIconProps,
          onPressBackButton: onPressBackButton
        }, backBtnAccessibilityProps, getTestId(testID))), _jsxs(View, Object.assign({
          style: modalStyles.headerContentLeft
        }, {
          children: [showHeaderTitle && _jsx(Typography, Object.assign({
            text: headerTitle,
            type: isLargeScreen ? 'body-para-xl-bold' : 'body-para-lg-bold',
            color: BLACK_900
          }, (headerTitleMultiText === null || headerTitleMultiText === void 0 ? void 0 : headerTitleMultiText.length) > 0 && {
            multiText: headerTitleMultiText
          }, headerAlignment === 'center' && {
            textAlignment: 'center'
          }, getTestId(testID, 'showHeaderTitle'), headerAccessibilityProps)), showSubTitle && checkSubTitleForLargeScreen && _jsx(Typography, Object.assign({
            style: modalStyles.subTitle,
            text: subTitle,
            type: isLargeScreen ? 'body-para-md' : 'body-para-sm',
            color: BLACK_700
          }, subTitleAccessibilityProps, headerAlignment === 'center' && {
            textAlignment: 'center'
          }, getTestId(testID, 'subTitle')))]
        })), _jsx(Cross, Object.assign({
          visible: showCross,
          onPress: () => close('close'),
          containerStyle: [modalStyles.crossContainerStyle, crossContainerStyle],
          crossIconProps: config === null || config === void 0 ? void 0 : config.crossIconProps
        }, crossIconAccessibilityProps, getTestId(testID)))]
      }))]
    }));
  };
  var onMaskPress = () => {
    if (closeOnPressMask) {
      close('swipe');
      onCloseFromMaskHandler === null || onCloseFromMaskHandler === void 0 ? void 0 : onCloseFromMaskHandler();
    }
  };
  var modalContent = () => {
    return _jsx(Animated.View, Object.assign({}, !dragFromTopOnly && panResponder.panHandlers, {
      style: animatedViewStyles
    }, {
      children: _jsxs(WrapperView, Object.assign({
        onLayout: handleChildrenLayout,
        style: [modalStyles.container, isLargeScreen && modalStyles.containerLargeScreen, containerStyle],
        activeOpacity: 1
      }, containerProps, {
        children: [_jsx(Header, {}), isScrollView ? _jsx(ScrollView, Object.assign({}, scrollViewProps, {
          keyboardShouldPersistTaps: "handled"
        }, {
          children: children
        })) : children, footerElement, showLottieOnModal && _jsx(Lottie, Object.assign({}, lottieOnModalProps, {
          style: [modalStyles.lottie, lottieOnModalProps === null || lottieOnModalProps === void 0 ? void 0 : lottieOnModalProps.style]
        }))]
      }))
    }));
  };
  return _jsx(RNModal, Object.assign({
    transparent: true,
    animationType: 'none',
    visible: isModalVisible,
    statusBarTranslucent: onTopOfStatusBar,
    onRequestClose: handleOnRequestClose,
    supportedOrientations: ['portrait']
  }, getTestId(testID), restProps, {
    children: _jsxs(KeyboardAvoidingView, Object.assign({
      enabled: keyboardAvoidingViewEnabled,
      keyboardVerticalOffset: 0,
      behavior: checkIsPlatform('android') ? undefined : 'padding',
      style: [modalStyles.wrapper, wrapperStyle]
    }, {
      children: [isLargeScreen ? _jsx(TouchableOpacity, Object.assign({
        style: modalStyles.maskLargeScreen,
        activeOpacity: 1,
        onPress: onMaskPress
      }, {
        children: modalContent()
      })) : _jsxs(_Fragment, {
        children: [_jsx(Pressable, Object.assign({
          style: modalStyles.mask,
          onPress: onMaskPress,
          accessible: false,
          importantForAccessibility: "no"
        }, checkIsPlatform('web') && {
          tabIndex: null
        })), modalContent()]
      }), globalElement]
    }))
  }));
});